import type {
    CalibratedRecord,
    CalibrationKind,
    CalibrationMap,
    Drone,
    DroneUsage,
    PartType,
    ServiceRecord
} from '../../types';
import { getEffectiveUsage } from '../getEffectiveUsage';

const MS_PER_DAY = 86400000;

const findLatestCalibration = ({
    kind,
    records
}: {
    kind: CalibrationKind;
    records: ServiceRecord[];
}): CalibratedRecord | null =>
    records.reduce<CalibratedRecord | null>((latest, record) => {
        if (record.voided || record.eventType !== 'calibrated') {
            return latest;
        }

        if (record.calibrationKind !== kind) {
            return latest;
        }

        if (!latest) {
            return record;
        }

        const isNewer =
            new Date(record.performedAt).getTime() > new Date(latest.performedAt).getTime();

        return isNewer ? record : latest;
    }, null);

// The newest non-voided record of any event type, by performedAt — what the drone
// card surfaces as the "last service" caption. Undefined when every record is voided.
const findLatestServiceRecord = ({
    records
}: {
    records: ServiceRecord[];
}): ServiceRecord | undefined =>
    records.reduce<ServiceRecord | undefined>((latest, record) => {
        if (record.voided) {
            return latest;
        }

        if (!latest) {
            return record;
        }

        const isNewer =
            new Date(record.performedAt).getTime() > new Date(latest.performedAt).getTime();

        return isNewer ? record : latest;
    }, undefined);

// Applies the drone's active service records to its counters and calibration state.
// Replaced/serviced records reset the targeted counter to the raw reading minus the
// latest snapshot (see getEffectiveUsage); the latest calibrated record per kind
// overlays the drone's calibration recency. Inspected records have no effect.
// Returns a new drone — never mutates the input.
export const applyServiceRecords = ({
    drone,
    records
}: {
    drone: Drone;
    records: ServiceRecord[];
}): Drone => {
    if (records.length === 0) {
        return drone;
    }

    const usage: DroneUsage = {};

    (Object.keys(drone.usage) as PartType[]).forEach(partType => {
        usage[partType] = getEffectiveUsage({
            partType,
            rawUsage: drone.usage[partType] ?? 0,
            records
        });
    });

    // FR-30b — props are virtual units sharing the airframe aggregate as their raw
    // counter: each prop resets against its own latest snapshot (falling back to the
    // aggregate record), and the aggregate view written back is the worst prop so
    // rings, recommendations, and every other aggregate consumer stay unchanged.
    const propSetRawUsage = drone.usage.propSet;
    const propSetUnitUsages =
        propSetRawUsage !== undefined && drone.motors.length > 0
            ? drone.motors.map((_, index) =>
                  getEffectiveUsage({
                      partType: 'propSet',
                      rawUsage: propSetRawUsage,
                      records,
                      unitNumber: index + 1
                  })
              )
            : undefined;

    if (propSetUnitUsages) {
        usage.propSet = propSetUnitUsages.reduce(
            (highest, unitUsage) => Math.max(highest, unitUsage),
            0
        );
    }

    const motors = drone.motors.map((motor, index) => ({
        ...motor,
        usage: getEffectiveUsage({
            partType: 'motor',
            rawUsage: motor.usage,
            records,
            unitNumber: index + 1
        })
    }));

    const pumps = drone.pumps.map((pump, index) => ({
        ...pump,
        usage: getEffectiveUsage({
            partType: 'pumpSealKit',
            rawUsage: pump.usage,
            records,
            unitNumber: index + 1
        })
    }));

    const flowMeter = {
        ...drone.flowMeter,
        usage: getEffectiveUsage({
            partType: 'flowMeter',
            rawUsage: drone.flowMeter.usage,
            records,
            unitNumber: 1
        })
    };

    // Recency is computed against the current clock on every call so it stays fresh
    // without memoizing Date.now() anywhere upstream.
    const now = Date.now();
    const calibration: CalibrationMap = { ...drone.calibration };

    (Object.keys(drone.calibration) as CalibrationKind[]).forEach(kind => {
        const latest = findLatestCalibration({ kind, records });

        if (!latest) {
            return;
        }

        calibration[kind] = {
            daysAgo: Math.floor((now - new Date(latest.performedAt).getTime()) / MS_PER_DAY),
            recorded: true
        };
    });

    return {
        ...drone,
        calibration,
        flowMeter,
        latestServiceRecord: findLatestServiceRecord({ records }),
        motors,
        propSetUnitUsages,
        pumps,
        usage
    };
};
